import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ArrowLeft, Check, Hand } from 'lucide-react';
import { settingsApi } from '../../api/settingsApi';
import { haptic } from '../../telegram';

// Живой интерактивный тур: подсветка реального элемента; на шагах-переходах
// пользователь сам нажимает подсвеченный элемент (дырка кликабельна), тур ловит
// смену экрана и идёт дальше. Информационные шаги листаются кнопкой «Далее».
interface Step {
  title: string;
  body: string;
  selector?: string;      // что подсветить
  interactive?: boolean;  // дырка кликабельна; ждём перехода на expectPath
  expectPath?: string;    // куда пользователь должен перейти
}

const STEPS: Step[] = [
  {
    title: 'Добро пожаловать 👋',
    body: 'Это ваша ИИ-команда в Telegram: опишите задачу словами — платформа сама подберёт агентов, соберёт сценарий и выполнит его. Ничего программировать не нужно. Покажу за минуту, что где.',
  },
  {
    selector: '[data-tour="nav-home"]',
    interactive: true, expectPath: '/',
    title: 'Главный экран',
    body: 'Начнём с главного — нажмите подсвеченную кнопку «Главная» внизу.',
  },
  {
    selector: '.rf-hero',
    title: 'Оркестратор — главный способ',
    body: 'Это Ассистент-оркестратор: он управляет агентами и сценариями. Откройте его и опишите задачу словами — например «каждое утро присылай дайджест новостей». Он задаст пару уточнений и сам всё соберёт.',
  },
  {
    selector: '.rf-act.primary',
    title: 'Конструктор сценариев',
    body: 'Если хотите собрать автоматизацию по шагам — здесь пошаговый конструктор с выбором триггера, действий и расписания.',
  },
  {
    selector: '[data-tour="nav-market"]',
    interactive: true, expectPath: '/marketplace',
    title: 'Маркетплейс',
    body: 'Теперь заглянем в маркетплейс — нажмите подсвеченную кнопку «Маркетплейс» внизу.',
  },
  {
    selector: '.rf-ptitle',
    title: 'Готовые решения',
    body: 'Маркетплейс — готовые сценарии и агенты. Установите к себе в пару касаний или опубликуйте свой сценарий для других.',
  },
  {
    selector: '[data-tour="nav-more"]',
    interactive: true, expectPath: '/settings',
    title: 'Настройки команды',
    body: 'И раздел «Ещё» — нажмите подсвеченную кнопку с шестерёнкой внизу справа.',
  },
  {
    selector: '[data-tour="/settings/agents"]',
    title: 'Агенты',
    body: 'Агенты — специалисты под конкретные задачи. Можно создать своего или дать оркестратору собрать его автоматически.',
  },
  {
    selector: '[data-tour="/settings/connections"]',
    title: 'Связи',
    body: 'Подключение внешних сервисов: CRM, Notion, Slack, Telegram и другие. Через связи агенты действуют прямо в ваших системах.',
  },
  {
    selector: '[data-tour="/settings/knowledge"]',
    title: 'База знаний',
    body: 'Ваши документы и память команды. Агенты отвечают и действуют с учётом ваших данных, а не «вообще».',
  },
  {
    selector: '[data-tour="/settings/tasks"]',
    title: 'Задачи и напоминания',
    body: 'Отложенные сценарии: действие по расписанию (например, отчёт по понедельникам) или напоминание в нужный момент.',
  },
  {
    selector: '[data-tour="/settings/byok"]',
    title: 'Провайдеры и ключи',
    body: 'Провайдер платформы уже подключён — платить и настраивать ничего не нужно. Хотите свою модель — добавьте свой ключ здесь.',
  },
  {
    selector: '[data-tour="__tour__"]',
    title: 'Всё готово! 🎉',
    body: 'Этот тур можно пройти снова отсюда в любой момент. Удачи — и опишите первую задачу словами!',
  },
];

interface TourCtx { start: () => void; stop: () => void; }
const Ctx = createContext<TourCtx>({ start: () => {}, stop: () => {} });
export const useTour = () => useContext(Ctx);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [running, setRunning] = useState(false);
  const start = useCallback(() => { haptic('medium'); setRunning(true); }, []);
  // Тур обязан гаснуть, если поверх него открылся онбординг/согласие:
  // например, «Начать заново» нажали при уже открытом мини-аппе.
  const stop = useCallback(() => { setRunning(false); }, []);
  return (
    <Ctx.Provider value={{ start, stop }}>
      {children}
      {running && <TourOverlay onClose={() => setRunning(false)} />}
    </Ctx.Provider>
  );
}

function TourOverlay({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const enteredPath = useRef(location.pathname);
  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  // Аналитика онбординга: сообщаем максимальный достигнутый шаг тура
  // (fire-and-forget; сервер берёт GREATEST, порядок запросов не важен).
  // По нему видно, где пользователь бросил тур, даже если просто закрыл приложение.
  const maxSentStep = useRef(-1);
  useEffect(() => {
    if (i > maxSentStep.current) {
      maxSentStep.current = i;
      settingsApi.saveProfile({ guide_step: i } as any).catch(() => {});
    }
  }, [i]);

  // Поиск подсвечиваемого элемента (с повторами до монтирования). Экран НЕ переключаем сами.
  useEffect(() => {
    enteredPath.current = location.pathname;
    let cancelled = false;
    let tries = 0;
    const measure = () => {
      if (cancelled) return;
      if (!step.selector) { setRect(null); return; }
      const el = document.querySelector(step.selector) as HTMLElement | null;
      if (el) {
        try { el.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch {}
        const r = el.getBoundingClientRect();
        setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      } else if (tries < 30) { tries++; setTimeout(measure, 80); }
      else setRect(null);
    };
    setRect(null);
    const t = setTimeout(measure, 80);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Автопереход интерактивного шага: когда пользователь сам перешёл на нужный экран.
  useEffect(() => {
    if (step.interactive && step.expectPath && location.pathname === step.expectPath && enteredPath.current !== step.expectPath) {
      setI((v) => Math.min(v + 1, STEPS.length - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const onR = () => {
      if (!step.selector) return;
      const el = document.querySelector(step.selector) as HTMLElement | null;
      if (el) { const r = el.getBoundingClientRect(); setRect({ x: r.left, y: r.top, w: r.width, h: r.height }); }
    };
    window.addEventListener('resize', onR);
    window.addEventListener('scroll', onR, true);
    return () => { window.removeEventListener('resize', onR); window.removeEventListener('scroll', onR, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  const finish = async (completed: boolean) => {
    // «Готово» на последнем шаге = тур пройден до конца; «Пропустить» — только закрытие.
    try { await settingsApi.saveProfile({ guide_seen: true, ...(completed ? { guide_completed: true } : {}) } as any); } catch {}
    qc.invalidateQueries({ queryKey: ['profile'] });
    onClose();
  };
  const next = () => {
    haptic('light');
    if (last) { finish(true); return; }
    // На интерактивном шаге «Далее» = осознанный переход (экран назван в тексте).
    if (step.interactive && step.expectPath && location.pathname !== step.expectPath) nav(step.expectPath);
    setI((v) => Math.min(v + 1, STEPS.length - 1));
  };
  const prev = () => { haptic('light'); setI((v) => Math.max(0, v - 1)); };
  const skip = () => { haptic('medium'); finish(false); };

  const pad = 8;
  const hole = rect ? { x: Math.max(4, rect.x - pad), y: Math.max(4, rect.y - pad), w: rect.w + pad * 2, h: rect.h + pad * 2 } : null;
  const targetBottomHalf = hole ? (hole.y + hole.h / 2) > window.innerHeight / 2 : false;
  const block = (e: React.MouseEvent) => { e.stopPropagation(); };
  const dim: React.CSSProperties = { position: 'absolute', background: 'rgba(6,9,16,0.84)', pointerEvents: 'auto' };

  return createPortal(
    <div className="rf-tour">
      {hole ? (
        <>
          <div style={{ ...dim, left: 0, top: 0, right: 0, height: hole.y }} onClick={block} />
          <div style={{ ...dim, left: 0, top: hole.y + hole.h, right: 0, bottom: 0 }} onClick={block} />
          <div style={{ ...dim, left: 0, top: hole.y, width: hole.x, height: hole.h }} onClick={block} />
          <div style={{ ...dim, left: hole.x + hole.w, top: hole.y, right: 0, height: hole.h }} onClick={block} />
          {/* Информационный шаг: перекрываем дырку, чтобы случайно не уйти. Интерактивный — оставляем кликабельной. */}
          {!step.interactive && <div style={{ position: 'absolute', left: hole.x, top: hole.y, width: hole.w, height: hole.h, pointerEvents: 'auto' }} onClick={block} />}
          <div className="rf-tour-ring" style={{ left: hole.x, top: hole.y, width: hole.w, height: hole.h }} />
        </>
      ) : (
        <div style={{ ...dim, inset: 0 }} onClick={block} />
      )}

      <div className={'rf-tour-card' + (targetBottomHalf ? ' top' : '')}>
        <div className="rf-tour-step">{i + 1} из {STEPS.length}</div>
        <div className="rf-tour-title">{step.title}</div>
        <div className="rf-tour-body">{step.body}</div>
        {step.interactive && (
          <div className="rf-tour-hint"><Hand />Нажмите подсвеченный элемент — или «Далее»</div>
        )}
        <div className="rf-tour-actions">
          <button className="rf-tour-skip" onClick={skip}>Пропустить</button>
          <div className="rf-tour-nav">
            {i > 0 && <button className="rf-tour-btn" onClick={prev}><ArrowLeft />Назад</button>}
            <button className="rf-tour-btn primary" onClick={next}>{last ? <><Check />Готово</> : <>Далее<ArrowRight /></>}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
